// src/routes/MediaSelection/ScientificAnalysisSection.js

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  alpha
} from '@mui/material';
import { motion } from 'framer-motion';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import PublicIcon from '@mui/icons-material/Public';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupsIcon from '@mui/icons-material/Groups';

/**
 * 市場状況に応じた色を取得
 */
const getMarketStatusColor = (jobRatio) => {
  if (jobRatio >= 3.0) return '#f44336';  // 売り手市場（高難度）
  if (jobRatio >= 1.5) return '#ff9800';  // やや売り手市場
  if (jobRatio >= 0.8) return '#4caf50';  // バランス
  return '#2196F3';  // 買い手市場（採用しやすい）
};

/**
 * 市場状況のラベルを取得
 */
const getMarketStatusLabel = (jobRatio) => {
  if (jobRatio >= 3.0) return { status: '売り手市場', description: '求職者有利' };
  if (jobRatio >= 1.5) return { status: 'やや売り手市場', description: '競争あり' };
  if (jobRatio >= 0.8) return { status: 'バランス', description: '均衡状態' };
  return { status: '買い手市場', description: '採用しやすい' };
};

/**
 * 採用市場の概況カード
 */
function MarketOverviewCard({ assessment }) {
  if (!assessment) return null;

  const jobRatio = assessment.jobRatio || 1.0;
  const color = getMarketStatusColor(jobRatio);
  const marketStatus = getMarketStatusLabel(jobRatio);

  // 職種カテゴリを推測（factorsから取得、またはデフォルト）
  const jobCategory = assessment.jobCategory ||
    (assessment.factors?.find(f => f.factor === '職種カテゴリ')?.value) ||
    '一般職';

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.3)}`,
        backgroundColor: alpha(color, 0.02),
        height: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <PublicIcon sx={{ color, fontSize: 24 }} />
        <Typography variant="subtitle1" fontWeight={600} color={color}>
          採用市場の概況
        </Typography>
      </Box>

      {/* 職種カテゴリ */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          職種カテゴリ
        </Typography>
        <Typography variant="body1" fontWeight={600} color="text.primary">
          {jobCategory}
        </Typography>
      </Box>

      {/* 有効求人倍率 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          有効求人倍率
        </Typography>
        <Typography variant="h5" fontWeight={700} color={color}>
          {jobRatio.toFixed(2)}倍
        </Typography>
      </Box>

      {/* 市場状況 */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" color="text.secondary">
          市場状況
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Chip
            label={marketStatus.status}
            size="small"
            sx={{
              backgroundColor: alpha(color, 0.15),
              color: color,
              fontWeight: 600
            }}
          />
          <Typography variant="body2" color="text.secondary">
            （{marketStatus.description}）
          </Typography>
        </Box>
      </Box>

      {/* 市場コメント */}
      {assessment.marketComparison && (
        <Box sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: alpha(color, 0.05),
          border: `1px dashed ${alpha(color, 0.2)}`
        }}>
          <Typography variant="body2" color="text.secondary">
            {assessment.marketComparison}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

/**
 * 採用ファネル予測カード
 */
function FunnelPredictionCard({ prediction }) {
  if (!prediction) return null;

  const stages = [
    { label: '応募', value: prediction.expectedApplicants, color: '#2196F3' },
    { label: '書類通過', value: prediction.expectedScreeningPass, color: '#03A9F4' },
    { label: '面接', value: prediction.expectedInterviews, color: '#00BCD4' },
    { label: '内定', value: prediction.expectedOffers, color: '#009688' },
    { label: '採用', value: prediction.expectedHires, color: '#4CAF50' }
  ];

  const maxValue = stages[0].value || 1;

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha('#2196F3', 0.3),
        backgroundColor: alpha('#2196F3', 0.02),
        height: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ShowChartIcon sx={{ color: '#2196F3', fontSize: 24 }} />
        <Typography variant="subtitle1" fontWeight={600} color="#1976D2">
          採用ファネル予測
        </Typography>
        <Chip
          size="small"
          label={`信頼度: ${prediction.confidenceLevel || '中'}`}
          sx={{
            ml: 'auto',
            fontSize: '0.7rem',
            backgroundColor: alpha('#2196F3', 0.1),
            color: '#1976D2'
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {stages.map((stage, idx) => (
          <Box key={idx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={500}>
                {stage.label}
              </Typography>
              <Typography variant="body2" fontWeight={600} color={stage.color}>
                {stage.value}名
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(stage.value / maxValue) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha(stage.color, 0.15),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: stage.color,
                  borderRadius: 3
                }
              }}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          データソース: {prediction.dataSource || '業界ベンチマーク'}
        </Typography>
      </Box>
    </Paper>
  );
}

/**
 * チャネルミックス推奨カード
 */
function ChannelMixCard({ recommendation }) {
  if (!recommendation) return null;

  const { primaryChannel, secondaryChannels, budgetAllocation, rationale } = recommendation;

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha('#9C27B0', 0.3),
        backgroundColor: alpha('#9C27B0', 0.02),
        height: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <GroupsIcon sx={{ color: '#9C27B0', fontSize: 24 }} />
        <Typography variant="subtitle1" fontWeight={600} color="#7B1FA2">
          チャネルミックス推奨
        </Typography>
      </Box>

      {primaryChannel && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            主力チャネル
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={primaryChannel.type}
              color="secondary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
            <Typography variant="body2" fontWeight={600}>
              {primaryChannel.allocation}%
            </Typography>
          </Box>
          {primaryChannel.media && primaryChannel.media.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {primaryChannel.media.map((m, idx) => (
                <Chip key={idx} label={m} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              ))}
            </Box>
          )}
        </Box>
      )}

      {secondaryChannels && secondaryChannels.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            補助チャネル
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {secondaryChannels.map((ch, idx) => (
              <Chip
                key={idx}
                label={`${ch.type} ${ch.allocation}%`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {rationale && (
        <Box sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: alpha('#9C27B0', 0.05),
          border: `1px dashed ${alpha('#9C27B0', 0.2)}`
        }}>
          <Typography variant="caption" color="text.secondary">
            {rationale}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

/**
 * ROI予測カード
 */
function ROIPredictionCard({ prediction }) {
  if (!prediction) return null;

  const { estimatedCostPerHire, industryBenchmark, comparisonStatus, comparisonDetail, roi } = prediction;

  const getStatusIcon = () => {
    switch (comparisonStatus) {
      case '効率的':
        return <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />;
      case '要注意':
        return <WarningIcon sx={{ color: '#f44336', fontSize: 20 }} />;
      default:
        return <TrendingUpIcon sx={{ color: '#FF9800', fontSize: 20 }} />;
    }
  };

  const getStatusColor = () => {
    switch (comparisonStatus) {
      case '効率的':
        return '#4CAF50';
      case '要注意':
        return '#f44336';
      default:
        return '#FF9800';
    }
  };

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha('#FF9800', 0.3),
        backgroundColor: alpha('#FF9800', 0.02),
        height: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AccountBalanceWalletIcon sx={{ color: '#FF9800', fontSize: 24 }} />
        <Typography variant="subtitle1" fontWeight={600} color="#F57C00">
          ROI予測・コスト分析
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            予測採用単価
          </Typography>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            ¥{(estimatedCostPerHire || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            業界平均
          </Typography>
          <Typography variant="h5" fontWeight={700} color="text.secondary">
            ¥{(industryBenchmark || 0).toLocaleString()}
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: alpha(getStatusColor(), 0.1)
      }}>
        {getStatusIcon()}
        <Box>
          <Typography variant="body2" fontWeight={600} color={getStatusColor()}>
            {comparisonStatus}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {comparisonDetail}
          </Typography>
        </Box>
      </Box>

      {roi !== undefined && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            投資対効果 (ROI)
          </Typography>
          <Typography variant="h6" fontWeight={600} color={roi > 0 ? '#4CAF50' : '#f44336'}>
            {roi > 0 ? '+' : ''}{roi.toFixed(1)}%
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

/**
 * 科学的分析セクション
 */
export default function ScientificAnalysisSection({ scientificAnalysis }) {
  if (!scientificAnalysis) return null;

  const {
    difficultyAssessment,
    funnelPrediction,
    channelMixRecommendation,
    roiPrediction
  } = scientificAnalysis;

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{ mb: 4 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AnalyticsIcon sx={{ color: '#1976D2', fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700} color="text.primary">
          採用予測分析
        </Typography>
        <Chip
          size="small"
          label="データ分析"
          sx={{
            ml: 1,
            backgroundColor: alpha('#1976D2', 0.1),
            color: '#1976D2',
            fontWeight: 600,
            fontSize: '0.7rem'
          }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        業界ベンチマークと過去データに基づく採用計画の予測分析です。
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <MarketOverviewCard assessment={difficultyAssessment} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FunnelPredictionCard prediction={funnelPrediction} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ChannelMixCard recommendation={channelMixRecommendation} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ROIPredictionCard prediction={roiPrediction} />
        </Grid>
      </Grid>
    </Box>
  );
}
